import { computed } from "vue"
import { useStore } from "vuex"
import { EntityType } from "@/interfaces/global/content"
import { WidgetTypes } from '@/interfaces/global/widgets'

export default function useDetailPage() {
  const store = useStore()

  const isWidget = computed(() => store.getters['widgets/isWidget'])
  const widgetType = computed(() => store.getters['widgets/widgetType'])
  const isLibraryWidget = computed(() => widgetType.value === WidgetTypes.library)

  const content = computed(() => store.state['content'])
  const isPart = computed(() => content.value.type === EntityType.part)
  const isPage = computed(() => content.value.type === EntityType.page)
  const isMedia = computed(() => content.value.type === EntityType.media)
  const isProductPage = computed(() => content.value.type === EntityType.productPage)

  const hideEmptyCards = computed(() => store.state.user.autoHideEmptyCards)

  const commentsTotal = computed(() => store.state.content.comments.total)
  const canViewComments = computed(() => store.getters['user/canViewComments'])
  const canEditAllComments = computed(() => store.getters['user/canEditAllComments'])
  const canAddAndEditComments = computed(() => store.getters['user/canAddAndEditComments'])
  const showComments =  computed(() => {
    if (canEditAllComments.value || canAddAndEditComments.value) {
      return true
    } else if (canViewComments.value) {
      return (commentsTotal.value > 0 || !hideEmptyCards.value)
    }
    return false
  })

  const canViewSuggestions = computed(() => {
    return store.getters['user/hasPartOrderSuggestions']
      && isPart.value
  })
  const suggestionsTotal = computed(() => store.state.content.suggestions.total)
  const showSuggestions = computed(() => {
    return canViewSuggestions.value
      && (suggestionsTotal.value > 0 || !hideEmptyCards.value)
  })

  const canViewRelated = computed(() => {
    if (isWidget.value) {
      return isLibraryWidget.value || widgetType.value === WidgetTypes.productPage
    }
    return store.getters['user/canViewRelated']
  })
  const relatedTotal = computed(() => store.state.content.related.total)
  const showRelated = computed(() => {
    return canViewRelated.value &&
      (relatedTotal.value > 0 || !hideEmptyCards.value)
  })
  const canViewAttachments = computed(() => {
    if (isWidget.value) {
      return true
    }
    return store.getters['user/canViewAttachments']
  })
  const attachmentsTotal = computed(() => store.state.content.attachments.total)
  const showAttachments = computed(() => {
    return canViewAttachments.value
      && (attachmentsTotal.value > 0 || !hideEmptyCards.value)
  })

  const whereUsedCount = computed(() => store.state.content.whereused.total)
  const canViewWhereUsed = computed(() => {
    return isMedia.value || isPart.value || isPage.value
  })
  const showWhereUsed = computed(() => {
    return canViewWhereUsed.value &&
      (whereUsedCount.value > 0 || !hideEmptyCards.value)
  })

  const infoTagsTotal = computed(() => store.state.content.tags.unfilteredTotal)
  const showInfoTags = computed(() => {
    return (hideEmptyCards.value && infoTagsTotal.value === 0)
      ? false
      : true
  })

  const partCollectionsTotal = computed(() => store.state.content.partCollectionsCount)
  const showPartCollections = computed(() => {
    return isProductPage.value
      && (partCollectionsTotal.value > 0 || !hideEmptyCards.value)
  })
  const hasPartCollections = computed(() => {
    return partCollectionsTotal.value > 0
  })

  return {
    content,
    isPart,
    isPage,
    showComments,
    canViewRelated,
    showRelated,
    showSuggestions,
    hideEmptyCards,
    canViewWhereUsed,
    showInfoTags,
    showAttachments,
    canViewAttachments,
    showWhereUsed,
    showPartCollections,
    hasPartCollections
  }
}
